import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { MusicNote } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { SonosSpeaker, isSonosPlaying } from '../../services/sonosApi';
import MarqueeWithBars from './MarqueeWithBars';
import MinimalVolumeBar from './MinimalVolumeBar';
import CircularProgressPlayButton from './CircularProgressPlayButton';
import AlbumArtImage from './AlbumArtImage';
import PlayingSourceLabel from './PlayingSourceLabel';
import { parseSonosTime } from './sonosPlaybackUtils';
import {
  modalSheetHandleBarSx,
  modalSheetHandleSx,
  modalSheetShellSx,
  SONOS_BLUE,
} from './sonosUiStyles';
import SonosGroupSpeakerIcon from './SonosGroupSpeakerIcon';

interface CompactPlayerBarProps {
  coordinator: SonosSpeaker | null;
  destinationSpeakers: SonosSpeaker[];
  canControl: boolean;
  playingSourceLabel?: string | null;
  onTransport: (action: 'play' | 'pause') => void;
  onOpenExpanded: () => void;
  onOpenDestination: () => void;
  onVolumeApplied: (speakerId: number, volume: number, mute: boolean, groupSpeakerIds?: number[]) => void;
  showPlaybackMedia?: boolean;
  onDismiss?: () => void;
}

const COMPACT_PLAYER_H = 170;
const COMPACT_PLAYER_BOTTOM = -5;
const COMPACT_SOURCE_ROW_H = 12;

const CompactPlayerBar: React.FC<CompactPlayerBarProps> = ({
  coordinator,
  destinationSpeakers,
  canControl,
  playingSourceLabel,
  onTransport,
  onOpenExpanded,
  onOpenDestination,
  onVolumeApplied,
  showPlaybackMedia = true,
  onDismiss,
}) => {
  const { t } = useTranslation();
  const hasSpeaker = Boolean(coordinator);
  const playing = isSonosPlaying(coordinator?.playback_state);
  const touchStartRef = useRef<{ x: number; y: number; time: number } | null>(null);
  const movedRef = useRef(false);
  const skipOpenRef = useRef(false);
  const handleDragRef = useRef<{ x: number; y: number; time: number; pointerId: number } | null>(null);
  const destCount = hasSpeaker ? destinationSpeakers.length : 0;
  const deviceLabel = !hasSpeaker
    ? t('musicSystem.playback.selectSpeaker')
    : destCount === 0
      ? '—'
      : destinationSpeakers.map((s) => s.nombre).join(' + ');
  const metaLine = showPlaybackMedia
    ? ([coordinator?.current_artist, coordinator?.current_album].filter(Boolean).join(' • ') || '')
    : '';
  const titleLine = showPlaybackMedia
    ? (coordinator?.current_title || '')
    : '';

  const basePosition = useMemo(
    () => parseSonosTime(coordinator?.track_position),
    [coordinator?.track_position],
  );
  const durationSec = useMemo(
    () => parseSonosTime(coordinator?.track_duration),
    [coordinator?.track_duration],
  );
  const [livePosition, setLivePosition] = useState<number | null>(basePosition);

  useEffect(() => {
    setLivePosition(basePosition);
  }, [basePosition, coordinator?.current_title]);

  useEffect(() => {
    if (!playing || livePosition == null) return undefined;
    const id = window.setInterval(() => {
      setLivePosition((prev) => (prev == null ? prev : prev + 1));
    }, 1000);
    return () => window.clearInterval(id);
  }, [playing, livePosition, durationSec]);

  const progress = durationSec && livePosition != null && durationSec > 0
    ? livePosition / durationSec
    : 0;
  const unifiedGroupIds = destCount >= 2 ? destinationSpeakers.map((s) => s.id) : undefined;

  const openPrimary = useCallback(() => {
    if (!hasSpeaker) onOpenDestination();
    else onOpenExpanded();
  }, [hasSpeaker, onOpenDestination, onOpenExpanded]);

  const handleVolume = (volume: number, mute: boolean) => {
    if (!coordinator) return;
    onVolumeApplied(coordinator.id, volume, mute, unifiedGroupIds);
  };

  const isControlTarget = (target: EventTarget | null) => {
    if (!(target instanceof HTMLElement)) return false;
    return Boolean(
      target.closest('[data-audio-player-control]')
      || target.closest('[data-audio-player-handle]')
      || target.closest('button'),
    );
  };

  const openIfTap = useCallback((dx: number, dy: number, dt: number) => {
    const absX = Math.abs(dx);
    const absY = Math.abs(dy);
    if (absY > 40 && absY > absX && dy < 0 && dt < 700) {
      openPrimary();
      return;
    }
    if (absX < 12 && absY < 12 && dt < 450) {
      openPrimary();
    }
  }, [openPrimary]);

  const handleTouchStart = (e: React.TouchEvent) => {
    if (handleDragRef.current || skipOpenRef.current) return;
    if (isControlTarget(e.target)) {
      skipOpenRef.current = true;
      touchStartRef.current = null;
      return;
    }
    skipOpenRef.current = false;
    const t0 = e.touches[0];
    touchStartRef.current = { x: t0.clientX, y: t0.clientY, time: Date.now() };
    movedRef.current = false;
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (skipOpenRef.current) return;
    const start = touchStartRef.current;
    if (!start) return;
    const t0 = e.touches[0];
    if (Math.abs(t0.clientX - start.x) > 8 || Math.abs(t0.clientY - start.y) > 8) {
      movedRef.current = true;
    }
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    if (skipOpenRef.current || isControlTarget(e.target)) {
      skipOpenRef.current = false;
      touchStartRef.current = null;
      movedRef.current = false;
      return;
    }
    const start = touchStartRef.current;
    if (!start) return;
    const t0 = e.changedTouches[0];
    const dx = t0.clientX - start.x;
    const dy = t0.clientY - start.y;
    const dt = Date.now() - start.time;
    if (!movedRef.current) {
      openIfTap(dx, dy, dt);
    } else if (dy < -40 && Math.abs(dy) > Math.abs(dx)) {
      openPrimary();
    }
    touchStartRef.current = null;
    movedRef.current = false;
  };

  const handleBodyClick = useCallback((e: React.MouseEvent) => {
    if (isControlTarget(e.target) || (e.target as HTMLElement).closest('[data-audio-no-swipe]')) return;
    openPrimary();
  }, [openPrimary]);

  const stopBubble = (e: React.SyntheticEvent) => {
    e.stopPropagation();
  };

  const openDestinationFromIcon = (e: React.SyntheticEvent) => {
    e.stopPropagation();
    onOpenDestination();
  };

  const finishHandleDrag = useCallback((dx: number, dy: number, dt: number) => {
    const absX = Math.abs(dx);
    const absY = Math.abs(dy);
    const vertical = absY > absX * 0.85;
    const flickDown = vertical && dy > 14 && dt < 500;
    const dragDown = vertical && dy > 22;
    const flickUp = vertical && dy < -22 && dt < 600;
    const dragUp = vertical && dy < -28;

    if (flickDown || dragDown) {
      onDismiss?.();
      return;
    }
    if (flickUp || dragUp) {
      openPrimary();
      return;
    }
    if (absX < 14 && absY < 14 && dt < 500) {
      openPrimary();
    }
  }, [onDismiss, openPrimary]);

  const handlePointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    e.stopPropagation();
    e.preventDefault();
    e.currentTarget.setPointerCapture(e.pointerId);
    handleDragRef.current = {
      x: e.clientX,
      y: e.clientY,
      time: Date.now(),
      pointerId: e.pointerId,
    };
    skipOpenRef.current = true;
  };

  const handlePointerUp = (e: React.PointerEvent<HTMLDivElement>) => {
    const start = handleDragRef.current;
    if (!start || start.pointerId !== e.pointerId) return;
    e.stopPropagation();
    if (e.currentTarget.hasPointerCapture(e.pointerId)) {
      e.currentTarget.releasePointerCapture(e.pointerId);
    }
    const dx = e.clientX - start.x;
    const dy = e.clientY - start.y;
    const dt = Date.now() - start.time;
    handleDragRef.current = null;
    skipOpenRef.current = false;
    finishHandleDrag(dx, dy, dt);
  };

  const handlePointerCancel = (e: React.PointerEvent<HTMLDivElement>) => {
    const start = handleDragRef.current;
    if (!start || start.pointerId !== e.pointerId) return;
    e.stopPropagation();
    if (e.currentTarget.hasPointerCapture(e.pointerId)) {
      e.currentTarget.releasePointerCapture(e.pointerId);
    }
    const dx = e.clientX - start.x;
    const dy = e.clientY - start.y;
    const dt = Date.now() - start.time;
    handleDragRef.current = null;
    skipOpenRef.current = false;
    if (dy > 10) finishHandleDrag(dx, dy, dt);
  };

  return (
    <Box
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      onClick={handleBodyClick}
      sx={{
        position: 'absolute',
        left: 0,
        right: 0,
        bottom: COMPACT_PLAYER_BOTTOM,
        zIndex: 20,
        minHeight: COMPACT_PLAYER_H,
        boxSizing: 'border-box',
        width: '100%',
        ...modalSheetShellSx,
        px: 1.25,
        pt: 0,
        pb: 0.95,
        cursor: 'pointer',
        touchAction: 'none',
      }}
    >
      <Box
        data-audio-player-handle=""
        onPointerDown={handlePointerDown}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerCancel}
        onClick={(e) => e.stopPropagation()}
        sx={{
          ...modalSheetHandleSx,
          mb: 0.35,
          mx: -1.25,
          px: 1.25,
          touchAction: 'none',
        }}
      >
        <Box sx={modalSheetHandleBarSx} />
      </Box>

      <Box sx={{ mt: '-17px', mb: 1.15, pr: '7.25rem', minHeight: COMPACT_SOURCE_ROW_H }}>
        {hasSpeaker && playingSourceLabel ? (
          <PlayingSourceLabel label={playingSourceLabel} variant="compact" />
        ) : null}
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.9, minWidth: 0, position: 'relative', minHeight: 56 }}>
        {hasSpeaker && coordinator ? (
          <AlbumArtImage
            speakerId={coordinator.id}
            artUrlKey={coordinator.current_album_art_url}
            titleKey={coordinator.current_title}
            artistKey={coordinator.current_artist}
            fetchArt={showPlaybackMedia}
            size={62}
            borderRadius={1.25}
            sx={{ mt: 0.1, flexShrink: 0 }}
          />
        ) : (
          <Box sx={{
            width: 62,
            height: 62,
            mt: 0.1,
            flexShrink: 0,
            borderRadius: 1.25,
            border: '1px solid rgba(255,255,255,0.08)',
            backgroundColor: '#0a0a0a',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
            <MusicNote sx={{ color: 'rgba(255,255,255,0.35)', fontSize: 36 }} />
          </Box>
        )}

        <Box sx={{ flex: 1, minWidth: 0, pt: 0.05, pr: 7.5 }}>
          <Typography sx={{
            color: hasSpeaker ? 'rgba(255,255,255,0.45)' : SONOS_BLUE,
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: '0.06em',
            textTransform: 'uppercase',
            lineHeight: 1.2,
            mb: 0.5,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}>
            {deviceLabel}
          </Typography>
          <Typography sx={{
            color: '#fff',
            fontSize: 18,
            fontWeight: 700,
            lineHeight: 1.2,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}>
            {hasSpeaker ? titleLine : ''}
          </Typography>
          <MarqueeWithBars
            text={hasSpeaker ? metaLine : ''}
            animating={Boolean(showPlaybackMedia && playing)}
            fontSize={12}
            sx={{ mt: 0.55, minHeight: 14 }}
          />
        </Box>

        <Box
          data-audio-player-control=""
          onClick={stopBubble}
          onTouchStart={stopBubble}
          onTouchEnd={stopBubble}
          sx={{
            position: 'absolute',
            right: 66,
            top: -22,
            flexShrink: 0,
          }}
        >
          <SonosGroupSpeakerIcon
            hasSpeaker={hasSpeaker}
            destCount={destCount}
            onClick={openDestinationFromIcon}
          />
        </Box>

        <Box
          data-audio-player-control=""
          sx={{
            position: 'absolute',
            right: 0,
            top: -12,
            flexShrink: 0,
          }}
          onClick={stopBubble}
          onTouchStart={stopBubble}
          onTouchEnd={stopBubble}
        >
          <CircularProgressPlayButton
            playing={playing}
            progress={progress}
            disabled={!hasSpeaker || !canControl}
            size={52}
            onClick={() => onTransport(playing ? 'pause' : 'play')}
          />
        </Box>
      </Box>

      <Box sx={{ mt: 1.1 }} data-audio-no-swipe="" onClick={stopBubble} onTouchStart={stopBubble}>
        {hasSpeaker && coordinator ? (
          <MinimalVolumeBar
            speakerId={coordinator.id}
            volume={coordinator.volume ?? 0}
            mute={coordinator.mute}
            disabled={!canControl}
            volumeTargetIds={unifiedGroupIds}
            onVolumeApplied={handleVolume}
          />
        ) : (
          <MinimalVolumeBar
            speakerId={0}
            volume={0}
            disabled
            onVolumeApplied={() => { /* noop */ }}
          />
        )}
      </Box>
    </Box>
  );
};

export { COMPACT_PLAYER_H, COMPACT_PLAYER_BOTTOM };
export default CompactPlayerBar;
